import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { 
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ClipboardIcon,
  BuildingOfficeIcon,
  CurrencyPoundIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { categoryService, Category } from '../services/categoryService';
import { ProductCategory, CATEGORY_LABELS } from '../types';

// Icon mapping from backend icon names to Heroicons components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ClipboardIcon,
  BuildingOfficeIcon,
  CurrencyPoundIcon,
  UserGroupIcon,
  ShieldCheckIcon,
};

// Default icon if category doesn't have one or it's not in the map
const DefaultIcon = DocumentTextIcon;

// Reverse mapping from category label to enum value
const getCategoryEnum = (categoryName: string): ProductCategory | null => {
  const entries = Object.entries(CATEGORY_LABELS) as [ProductCategory, string][];
  const found = entries.find(([_, label]) => label === categoryName);
  return found ? found[0] : null;
};

export const CategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await categoryService.getAllCategories();
        setCategories(response.categories || []);
      } catch (err: any) {
        console.error('Error loading categories:', err);
        setError(err.message || 'Failed to load categories. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  const getIcon = (iconName: string | null) => {
    if (!iconName) return DefaultIcon;
    const IconComponent = iconMap[iconName];
    return IconComponent || DefaultIcon;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header Section */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-secondary-900 mb-4">
          Browse Categories
        </h1>
        <p className="text-lg text-secondary-600 max-w-2xl mx-auto">
          Explore our comprehensive collection of care document categories. 
          Find the resources you need to support your care organization.
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600 text-lg">Loading categories...</p>
          </div>
        </div>
      ) : categories.length > 0 ? (
        <>
          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {categories.map((category) => {
              const IconComponent = getIcon(category.icon);
              const categoryEnum = getCategoryEnum(category.name);
              const categoryParam = categoryEnum || category.name;
              
              return (
                <Link
                  key={category.id}
                  to={`/marketplace?category=${encodeURIComponent(categoryParam)}`}
                  className="block"
                >
                  <Card hover className="h-full flex flex-col transition-all duration-200 hover:scale-105">
                    <div className="flex flex-col items-center text-center p-6 h-full">
                      {/* Icon */}
                      <div className="mb-4 p-4 bg-primary-50 rounded-full">
                        <IconComponent className="h-8 w-8 text-primary-600" />
                      </div>
                      
                      {/* Category Name */}
                      <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                        {category.name}
                      </h3>
                      
                      {/* Description */}
                      {category.description && (
                        <p className="text-sm text-secondary-600 mb-4 flex-grow">
                          {category.description}
                        </p>
                      )}
                      
                      {/* View Category Link */}
                      <div className="flex items-center text-primary-600 font-medium text-sm mt-auto">
                        <span>Explore</span>
                        <ArrowRightIcon className="h-4 w-4 ml-2" />
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>

          {/* Stats Section */}
          <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-8 text-center">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-secondary-900 mb-2">
                {categories.length} Categories Available
              </h2>
              <p className="text-secondary-600">
                Browse through our extensive collection of care document categories 
                to find exactly what your organization needs.
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-20">
          <div className="text-secondary-400 mb-4">
            <DocumentTextIcon className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-xl font-medium text-secondary-900 mb-2">
            No categories found
          </h3>
          <p className="text-secondary-600">
            Categories will appear here once they are added to the system.
          </p>
        </div>
      )}
    </div>
  );
};

